/**
 * DebtDueNudge — Phase 86.E Pass B.
 *
 * One line on the dashboard for whichever debt needs paying next, straight from
 * `urgency` in the canonical debt plan. Renders nothing unless a payment is
 * overdue or due inside the plan's urgency window, and says where the date came
 * from — including when it was worked out from payment history rather than the
 * lender.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { fetchDebtPlan } from '@/lib/debtPlanApi';
import { dueLabel, shortDate, SOURCE_LABEL } from '@/lib/debtPlanFormat';

export function DebtDueNudge() {
  const [next, setNext] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const plan = await fetchDebtPlan();
      if (cancelled || !plan?.urgency?.urgent) return;
      setNext(plan.urgency.next_payment);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!next) return null;

  return (
    <div
      className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${
        next.overdue
          ? 'border-red-200 bg-red-50/70'
          : 'border-gold/30 bg-gold/5'
      }`}
      data-testid="debt-due-nudge"
    >
      <p className="text-sm text-charcoal flex items-start gap-2 min-w-0">
        <Clock className={`h-4 w-4 mt-0.5 shrink-0 ${next.overdue ? 'text-red-500' : 'text-gold'}`} aria-hidden="true" />
        <span className="min-w-0">
          <strong className="font-medium">{next.name}</strong> is {dueLabel(next)}
          {next.amount ? <> · {formatMoney(next.amount, next.currency)}</> : null}
          <span className="text-muted-foreground text-xs">
            {' '}({shortDate(next.due_date)}
            {next.source ? `, ${SOURCE_LABEL[next.source] || next.source}` : ''})
          </span>
        </span>
      </p>
      <button
        type="button"
        onClick={() => navigate('/accounts?tab=liabilities')}
        className="text-xs font-medium text-evergreen inline-flex items-center gap-1 hover:underline"
        data-testid="debt-due-nudge-cta"
      >
        Open debt plan <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default DebtDueNudge;
